<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

import { useAccount } from 'dashboard/composables/useAccount';

import ChannelItem from 'dashboard/components/widgets/ChannelItem.vue';

const { t } = useI18n();
const router = useRouter();
const { accountId, currentAccount } = useAccount();

const globalConfig = useMapGetter('globalConfig/get');

const enabledFeatures = ref({});

const hasTiktokConfigured = computed(() => {
  return window.chatwootConfig?.tiktokAppId;
});

const hasThreadsConfigured = computed(() => {
  return window.chatwootConfig?.threadsAppId;
});

const hasXConfigured = computed(() => {
  return window.chatwootConfig?.xAppId;
});

const hasLinkedinConfigured = computed(() => {
  return window.chatwootConfig?.linkedinAppId;
});

const hasYoutubeConfigured = computed(() => {
  return window.chatwootConfig?.youtubeAppId;
});

const isPlayStoreEnabled = computed(() => true);

const isAppStoreEnabled = computed(() => true);

const channelList = computed(() => {
  const { apiChannelName } = globalConfig.value;
  const channels = [
    {
      key: 'website',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.DESCRIPTION'),
      icon: 'i-woot-website',
    },
    {
      key: 'facebook',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.DESCRIPTION'),
      icon: 'i-woot-messenger',
    },
    {
      key: 'whatsapp',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.DESCRIPTION'),
      icon: 'i-woot-whatsapp',
    },
    {
      key: 'sms',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.SMS.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.SMS.DESCRIPTION'),
      icon: 'i-woot-sms',
    },
    {
      key: 'email',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.DESCRIPTION'),
      icon: 'i-woot-mail',
    },
    {
      key: 'api',
      title: apiChannelName || t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.DESCRIPTION'),
      icon: 'i-woot-api',
    },
    {
      key: 'telegram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.DESCRIPTION'),
      icon: 'i-woot-telegram',
    },
    {
      key: 'line',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LINE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LINE.DESCRIPTION'),
      icon: 'i-woot-line',
    },
    {
      key: 'instagram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.DESCRIPTION'),
      icon: 'i-woot-instagram',
    },
  ];

  if (hasTiktokConfigured.value) {
    channels.push({
      key: 'tiktok',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TIKTOK.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TIKTOK.DESCRIPTION'),
      icon: 'i-woot-tiktok',
    });
  }

  if (hasThreadsConfigured.value) {
    channels.push({
      key: 'threads',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.THREADS.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.THREADS.DESCRIPTION'),
      icon: 'i-ri-threads-line',
    });
  }

  if (hasXConfigured.value) {
    channels.push({
      key: 'x',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.X.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.X.DESCRIPTION'),
      icon: 'i-ri-twitter-x-fill',
    });
  }

  if (hasLinkedinConfigured.value) {
    channels.push({
      key: 'linkedin',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LINKEDIN.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LINKEDIN.DESCRIPTION'),
      icon: 'i-ri-linkedin-fill',
    });
  }

  if (hasYoutubeConfigured.value) {
    channels.push({
      key: 'youtube',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.YOUTUBE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.YOUTUBE.DESCRIPTION'),
      icon: 'i-ri-youtube-fill',
    });
  }

  if (isPlayStoreEnabled.value) {
    channels.push({
      key: 'play_store',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.PLAY_STORE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.PLAY_STORE.DESCRIPTION'),
      icon: 'i-ri-google-play-fill',
    });
  }

  if (isAppStoreEnabled.value) {
    channels.push({
      key: 'app_store',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.APP_STORE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.APP_STORE.DESCRIPTION'),
      icon: 'i-ri-app-store-fill',
    });
  }

  channels.push({
    key: 'voice',
    title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VOICE.TITLE'),
    description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VOICE.DESCRIPTION'),
    icon: 'i-woot-voice',
  });

  return channels;
});

const initializeEnabledFeatures = async () => {
  enabledFeatures.value = currentAccount.value.features;
};

const initChannelAuth = channel => {
  const params = {
    sub_page: channel,
    accountId: accountId.value,
  };
  router.push({ name: 'settings_inboxes_page_channel', params });
};

onMounted(() => {
  initializeEnabledFeatures();
});
</script>

<template>
  <div
    class="grid max-w-3xl grid-cols-1 xs:grid-cols-2 mx-0 gap-6 sm:grid-cols-3 p-8"
  >
    <ChannelItem
      v-for="channel in channelList"
      :key="channel.key"
      :channel="channel"
      :enabled-features="enabledFeatures"
      @channel-item-click="initChannelAuth"
    />
  </div>
</template>
